import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Search, 
  Eye, 
  Download,
  CheckCircle2,
  Clock,
  FileText,
  Filter
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    filterSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissions, searchTerm, statusFilter, templateFilter]);

  const loadSubmissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/submissions`);
      setSubmissions(response.data);
      setFilteredSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = [...submissions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.company_name?.toLowerCase().includes(term) ||
        s.template_name?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => 
        statusFilter === 'completed' ? s.is_completed : !s.is_completed
      );
    }

    // Template filter
    if (templateFilter !== 'all') {
      filtered = filtered.filter(s => s.template_name === templateFilter);
    }

    setFilteredSubmissions(filtered);
  };

  const uniqueTemplates = [...new Set(submissions.map(s => s.template_name))];

  const handleExportCSV = () => {
    const headers = ['Company', 'Template', 'Status', 'Created', 'Updated'];
    const rows = filteredSubmissions.map(s => [
      s.company_name,
      s.template_name,
      s.is_completed ? 'Completed' : 'In Progress',
      new Date(s.created_at).toLocaleDateString(),
      new Date(s.updated_at).toLocaleDateString()
    ]);

    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Exported to CSV');
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <PageHeader
          title="All Submissions"
          description="View and manage client compliance document submissions."
          actions={
            <Button onClick={handleExportCSV} variant="outline" data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          }
        />

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by company or template..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
              </SelectContent>
            </Select>

            <Select value={templateFilter} onValueChange={setTemplateFilter}>
              <SelectTrigger className="w-full md:w-[220px]" data-testid="template-filter">
                <SelectValue placeholder="Filter by template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Templates</SelectItem>
                {uniqueTemplates.map(template => (
                  <SelectItem key={template} value={template}>{template}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-slate-500">
          Showing {filteredSubmissions.length} of {submissions.length} submissions
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Loading submissions...</p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No submissions found</h3>
              <p className="text-slate-500">Try adjusting your filters.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Template</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id} data-testid={`submission-row-${submission.id}`}>
                    <td>
                      <div className="font-medium text-slate-900">{submission.company_name}</div>
                    </td>
                    <td>
                      <div className="text-slate-600">{submission.template_name}</div>
                    </td>
                    <td>
                      <span className={submission.is_completed ? 'status-success' : 'status-pending'}>
                        {submission.is_completed ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Completed
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            In Progress
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="text-slate-500 text-sm">
                      {new Date(submission.created_at).toLocaleDateString()}
                    </td>
                    <td className="text-slate-500 text-sm">
                      {new Date(submission.updated_at).toLocaleDateString()}
                    </td>
                    <td>
                      <Link to={`/admin/submissions/${submission.id}`}>
                        <Button variant="ghost" size="sm" data-testid={`view-btn-${submission.id}`}>
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
